// ============================================================
//  DAY-OFF REQUESTS TAB — Admin review panel
//  Reads from Firebase: dayoff_requests/{id}
//  Accept: sets status + auto-inserts into rb_slots crewOff
//  Decline: sets status + optional reason note
// ============================================================

import React, { useState, useEffect } from "react";
import { ref, onValue, set as fbSet, get as fbGet } from "firebase/database";
import { db } from "../../firebase/config";
import { useProfileStore } from "../../store/profileStore";
import { showToast } from "../shared/Toast";

export interface DayOffRequest {
  id: string;
  profileId: string;
  crewName: string;
  role: string;
  requestDate: string; // YYYY-MM-DD
  note: string;
  status: "pending" | "accepted" | "declined" | "cancelled";
  declineReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

const EMPTY_SLOT = {
  driverId: null,
  sidemanId: null,
  startTime: "07:00",
  note: "",
  driverNote: "",
  sidemanNote: "",
  crewOff: [],
  standby: [],
  timeDuration: "",
  sideman2Id: null,
  sideman2Note: "",
};

function fmtRequestDate(dateKey: string): string {
  const d = new Date(dateKey + "T00:00:00");
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Main Component ───────────────────────────────────────────
const DayOffRequestTab: React.FC = () => {
  const profiles = useProfileStore((s) => s.profiles);
  const [requests, setRequests] = useState<DayOffRequest[]>([]);
  const [declineId, setDeclineId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  const revokeRequest = async (req: DayOffRequest) => {
    if (
      !window.confirm(
        `Cancel ${req.crewName}'s day-off on ${fmtRequestDate(req.requestDate)}?`,
      )
    )
      return;
    try {
      await fbSet(ref(db, `dayoff_requests/${req.id}/status`), "cancelled");
      await fbSet(
        ref(db, `dayoff_requests/${req.id}/reviewedAt`),
        new Date().toISOString(),
      );
      // Remove from rb_slots crewOff
      const slotsRef = ref(db, `rb_slots/${req.requestDate}`);
      const snap = await fbGet(slotsRef);
      const rawSlots = snap.val();
      if (rawSlots) {
        let slots: any[] = Array.isArray(rawSlots)
          ? [...rawSlots]
          : Array.from(
              {
                length:
                  Math.max(
                    ...Object.keys(rawSlots)
                      .map(Number)
                      .filter((n) => !isNaN(n)),
                  ) + 1,
              },
              (_, i) => rawSlots[i] || null,
            ).filter(Boolean);
        slots = slots.map((s: any) =>
          s
            ? {
                ...s,
                crewOff: (s.crewOff || []).filter(
                  (e: any) => e.id !== req.profileId,
                ),
              }
            : s,
        );
        await fbSet(slotsRef, slots);
      }
      showToast("Day-off cancelled ✓");
    } catch (e) {
      showToast("Error cancelling — try again");
    }
  };

  useEffect(() => {
    const rRef = ref(db, "dayoff_requests");
    const unsub = onValue(rRef, (snap) => {
      const raw = snap.val();
      if (!raw) {
        setRequests([]);
        return;
      }
      const list = (Object.values(raw) as DayOffRequest[]).sort((a, b) => {
        if (a.status === "pending" && b.status !== "pending") return -1;
        if (b.status === "pending" && a.status !== "pending") return 1;
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });
      setRequests(list);
    });
    return () => unsub();
  }, []);

  const acceptRequest = async (req: DayOffRequest) => {
    try {
      await fbSet(ref(db, `dayoff_requests/${req.id}/status`), "accepted");
      await fbSet(
        ref(db, `dayoff_requests/${req.id}/reviewedAt`),
        new Date().toISOString(),
      );

      // ── Auto-add to rb_slots crewOff for the request date ──
      const slotsRef = ref(db, `rb_slots/${req.requestDate}`);
      const snap = await fbGet(slotsRef);
      const rawSlots = snap.val();
      let slots: any[];

      if (rawSlots == null) {
        slots = Array.from({ length: 50 }, () => ({
          ...EMPTY_SLOT,
          crewOff: [],
          standby: [],
        }));
      } else if (Array.isArray(rawSlots)) {
        slots = rawSlots.map((s: any) =>
          s
            ? { ...EMPTY_SLOT, crewOff: [], standby: [], ...s }
            : { ...EMPTY_SLOT, crewOff: [], standby: [] },
        );
      } else if (typeof rawSlots === "object") {
        const keys = Object.keys(rawSlots)
          .map(Number)
          .filter((n) => !isNaN(n));
        const maxIdx = keys.length > 0 ? Math.max(...keys) : 49;
        slots = Array.from({ length: Math.max(50, maxIdx + 1) }, (_, i) =>
          rawSlots[i]
            ? { ...EMPTY_SLOT, crewOff: [], standby: [], ...rawSlots[i] }
            : { ...EMPTY_SLOT, crewOff: [], standby: [] },
        );
      } else {
        slots = Array.from({ length: 50 }, () => ({
          ...EMPTY_SLOT,
          crewOff: [],
          standby: [],
        }));
      }

      // Normalize crewOff/standby arrays
      slots = slots.map((s: any) => ({
        ...s,
        crewOff: Array.isArray(s.crewOff) ? s.crewOff : [],
        standby: Array.isArray(s.standby) ? s.standby : [],
      }));

      const profile = profiles.find((p) => p.id === req.profileId);
      const label: "D" | "S" = profile?.role === "driver" ? "D" : "S";

      const alreadyIn = slots.some((s: any) =>
        (s.crewOff || []).some((e: any) => e.id === req.profileId),
      );
      if (!alreadyIn) {
        slots[0] = {
          ...slots[0],
          crewOff: [...(slots[0].crewOff || []), { id: req.profileId, label }],
        };
        await fbSet(slotsRef, slots);
      }

      showToast("Day-off request accepted ✓");
    } catch (e) {
      console.error("Accept error:", e);
      showToast("Error accepting — try again");
    }
  };

  const submitDecline = async () => {
    if (!declineId) return;
    try {
      await fbSet(ref(db, `dayoff_requests/${declineId}/status`), "declined");
      await fbSet(
        ref(db, `dayoff_requests/${declineId}/declineReason`),
        declineReason.trim() || null,
      );
      await fbSet(
        ref(db, `dayoff_requests/${declineId}/reviewedAt`),
        new Date().toISOString(),
      );
      setDeclineId(null);
      setDeclineReason("");
      showToast("Day-off request declined");
    } catch {
      showToast("Error — try again");
    }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const reviewed = requests.filter((r) => r.status !== "pending");

  return (
    <div
      style={{
        padding: "16px 20px",
        fontFamily: "'Barlow',sans-serif",
        maxWidth: 900,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: 22,
          fontWeight: 800,
          textTransform: "uppercase",
          color: "#e8e9eb",
          marginBottom: 4,
        }}
      >
        Day-Off Requests
      </div>
      <div style={{ fontSize: 12, color: "#7a7d85", marginBottom: 20 }}>
        Review, accept, or decline crew day-off requests.
      </div>

      {requests.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#454850",
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            style={{ margin: "0 auto 12px", display: "block" }}
          >
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="#454850"
              strokeWidth="1.5"
            />
            <path
              d="M16 2v4M8 2v4M3 10h18"
              stroke="#454850"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          No day-off requests yet
        </div>
      )}

      {pending.length > 0 && (
        <>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              color: "#E85520",
              marginBottom: 10,
            }}
          >
            Pending ({pending.length})
          </div>
          {pending.map((req) => {
            const p = profiles.find((x) => x.id === req.profileId);
            return (
              <RequestCard
                key={req.id}
                req={req}
                profileDetails={
                  p
                    ? {
                        phone: p.phone || null,
                        username: (p as any).username || null,
                        userId: p.age || null,
                      }
                    : undefined
                }
                onAccept={() => acceptRequest(req)}
                onDecline={() => {
                  setDeclineId(req.id);
                  setDeclineReason("");
                }}
              />
            );
          })}
        </>
      )}

      {reviewed.length > 0 && (
        <>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              color: "#7a7d85",
              marginBottom: 10,
              marginTop: pending.length > 0 ? 24 : 0,
            }}
          >
            Reviewed ({reviewed.length})
          </div>
          {reviewed.map((req) => {
            const p = profiles.find((x) => x.id === req.profileId);
            return (
              <RequestCard
                key={req.id}
                req={req}
                profileDetails={
                  p
                    ? {
                        phone: p.phone || null,
                        username: (p as any).username || null,
                        userId: p.age || null,
                      }
                    : undefined
                }
                onCancel={
                  req.status === "accepted"
                    ? () => revokeRequest(req)
                    : undefined
                }
              />
            );
          })}
        </>
      )}

      {/* ── Decline modal ── */}
      {declineId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setDeclineId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#141518",
              border: "1px solid rgba(224,82,82,0.4)",
              borderRadius: 16,
              padding: "28px 24px",
              minWidth: 320,
              boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
            }}
          >
            <div
              style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: 18,
                fontWeight: 800,
                color: "#e8e9eb",
                marginBottom: 6,
              }}
            >
              Decline Request
            </div>
            <div style={{ fontSize: 12, color: "#7a7d85", marginBottom: 14 }}>
              Optionally provide a reason — the crew member will see this.
            </div>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Reason (optional)…"
              rows={3}
              autoFocus
              style={{
                width: "100%",
                background: "#1c1e22",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                padding: "9px 12px",
                color: "#e8e9eb",
                fontFamily: "'Barlow',sans-serif",
                fontSize: 13,
                outline: "none",
                resize: "none",
                boxSizing: "border-box",
                marginBottom: 12,
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setDeclineId(null)}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 8,
                  color: "#7a7d85",
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitDecline}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "#e05252",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                Confirm Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Request Card ─────────────────────────────────────────────
interface RequestCardProps {
  req: DayOffRequest;
  profileDetails?: {
    phone: string | null;
    username: string | null;
    userId: string | null;
  };
  onAccept?: () => void;
  onDecline?: () => void;
  onCancel?: () => void;
}
const RequestCard: React.FC<RequestCardProps> = ({
  req,
  profileDetails,
  onAccept,
  onDecline,
  onCancel,
}) => {
  const isPending = req.status === "pending";
  const isAccepted = req.status === "accepted";
  const isCancelled = req.status === "cancelled";
  const statusColor = isAccepted
    ? "#3ecf6e"
    : isPending
      ? "#E85520"
      : isCancelled
        ? "#7a7d85"
        : "#e05252";
  const statusLabel = isPending
    ? "Pending"
    : isAccepted
      ? "Accepted ✓"
      : isCancelled
        ? "Cancelled"
        : "Declined";
  const statusBg = isAccepted
    ? "rgba(62,207,110,0.1)"
    : isPending
      ? "rgba(232,85,32,0.1)"
      : isCancelled
        ? "rgba(122,125,133,0.1)"
        : "rgba(224,82,82,0.1)";

  return (
    <div
      style={{
        background: "#141518",
        border: `1px solid ${isPending ? "rgba(232,85,32,0.2)" : "rgba(255,255,255,0.06)"}`,
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: 12,
        padding: "16px",
        marginBottom: 10,
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 800, color: "#e8e9eb" }}>
            {req.crewName}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              color: req.role === "driver" ? "#E85520" : "#3ecf6e",
              background:
                req.role === "driver"
                  ? "rgba(232,85,32,0.1)"
                  : "rgba(62,207,110,0.1)",
              border: `1px solid ${req.role === "driver" ? "rgba(232,85,32,0.3)" : "rgba(62,207,110,0.3)"}`,
              borderRadius: 6,
              padding: "2px 7px",
            }}
          >
            {req.role}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              color: statusColor,
              background: statusBg,
              borderRadius: 6,
              padding: "2px 7px",
              border: `1px solid ${statusColor}40`,
            }}
          >
            {statusLabel}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 6,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="#7a7d85"
              strokeWidth="1.5"
            />
            <path
              d="M16 2v4M8 2v4M3 10h18"
              stroke="#7a7d85"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#e8e9eb" }}>
            {fmtRequestDate(req.requestDate)}
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#c8c9cb",
            marginBottom: 4,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: "#7a7d85" }}>Reason: </strong>
          {req.note}
        </div>
        {profileDetails && (
          <div
            style={{
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              fontSize: 11,
              color: "#7a7d85",
              marginBottom: 6,
              marginTop: 2,
            }}
          >
            {profileDetails.username && (
              <span>
                👤{" "}
                <strong style={{ color: "#e8e9eb" }}>
                  {profileDetails.username}
                </strong>
              </span>
            )}
            {profileDetails.userId && (
              <span>
                🪪{" "}
                <strong style={{ color: "#e8e9eb" }}>
                  {profileDetails.userId}
                </strong>
              </span>
            )}
            {profileDetails.phone && (
              <span>
                📞{" "}
                <strong style={{ color: "#e8e9eb" }}>
                  {profileDetails.phone}
                </strong>
              </span>
            )}
          </div>
        )}
        {req.status === "declined" && (
          <div
            style={{
              fontSize: 11,
              color: "#e05252",
              marginTop: 4,
              padding: "6px 10px",
              background: "rgba(224,82,82,0.07)",
              borderRadius: 6,
              border: "1px solid rgba(224,82,82,0.2)",
            }}
          >
            <strong>Admin note:</strong>{" "}
            {req.declineReason || "No reason has been given"}
          </div>
        )}
        <div style={{ fontSize: 10, color: "#454850", marginTop: 6 }}>
          Requested:{" "}
          {new Date(req.createdAt).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
          {req.reviewedAt &&
            ` · Reviewed: ${new Date(req.reviewedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}`}
        </div>
      </div>
      {isPending && onAccept && onDecline && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <button
            onClick={onAccept}
            style={{
              padding: "9px 18px",
              background: "rgba(62,207,110,0.1)",
              border: "1px solid rgba(62,207,110,0.4)",
              borderRadius: 8,
              color: "#3ecf6e",
              fontFamily: "'Barlow',sans-serif",
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            ✓ Accept
          </button>
          <button
            onClick={onDecline}
            style={{
              padding: "9px 18px",
              background: "rgba(224,82,82,0.1)",
              border: "1px solid rgba(224,82,82,0.4)",
              borderRadius: 8,
              color: "#e05252",
              fontFamily: "'Barlow',sans-serif",
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            ✕ Decline
          </button>
        </div>
      )}
      {isAccepted && onCancel && (
        <div style={{ flexShrink: 0 }}>
          <button
            onClick={onCancel}
            style={{
              padding: "9px 14px",
              background: "rgba(122,125,133,0.1)",
              border: "1px solid rgba(122,125,133,0.35)",
              borderRadius: 8,
              color: "#7a7d85",
              fontFamily: "'Barlow',sans-serif",
              fontSize: 11,
              fontWeight: 800,
              textTransform: "uppercase",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            ✕ Cancel Day-Off
          </button>
        </div>
      )}
    </div>
  );
};

export default DayOffRequestTab;
